import { IAdvertiserFormData, IAdvertiserPayload } from '@/Interfaces/Advertiser.interface';
import { IClientControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { handleApiValidationErrors } from '@/helpers/form';
import { createAdvertiser } from '@/store/api/advertisers.api';
import { Button, Dialog, DialogContent } from '@mui/material';
import { isAxiosError } from 'axios';
import { useCallback } from 'react';
import { FormProvider, UseFormReturn, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import AdvertiserInfo from './AdvertiserInfo';

interface IProps {
  form: UseFormReturn<IAdvertiserFormData>;
  open: boolean;
  onClose: (advertiser: IControlOption | null, refresh?: boolean) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const AddAdvertiserModal = ({ form, open, onClose, loading, setLoading }: IProps) => {
  const { control, handleSubmit, reset, setError } = form;

  const selectedClient: IClientControlOption | null = useWatch({ control, name: 'client' });

  const createNewAdvertiser = useCallback(
    async (payload: IAdvertiserPayload) => {
      setLoading(true);
      let createdAdvertiser: IControlOption | null = null;
      try {
        const result = await createAdvertiser(payload);
        createdAdvertiser = {
          value: result.id,
          label: result.name,
        };
        toast.success('Advertiser created successfully');
        reset();
      } catch (error) {
        if (isAxiosError(error) && error.response?.data?.errors?.length) {
          handleApiValidationErrors(error.response.data.errors, setError);
        } else if (isAxiosError(error)) {
          toast.error(error?.response?.data?.message || 'Unknown Internal Error');
        } else {
          toast.error((error as Error).message || 'Something went wrong');
        }
      } finally {
        setLoading(false);
      }
      return createdAdvertiser;
    },
    [setLoading, reset, setError],
  );

  const onSubmit = useCallback(
    async ({ name, category, domain }: IAdvertiserFormData) => {
      const payload: IAdvertiserPayload = {
        name,
        domain,
        clientId: selectedClient!.value,
        category: category!.value,
        agencyId: null,
      };
      const createdAdvertiser = await createNewAdvertiser(payload);
      if (createdAdvertiser) {
        onClose(createdAdvertiser, true);
      }
    },
    [createNewAdvertiser, onClose, selectedClient],
  );

  const handleClose = useCallback(() => {
    onClose(null);
    reset();
  }, [onClose, reset]);

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Dialog maxWidth={false} open={open} onClose={handleClose} aria-labelledby='modal-title'>
        <DialogContent sx={{ width: '650px', overflow: 'hidden' }}>
          <ModalTitleCard titleIcon={<ModalHeaderIcon />} handleClose={handleClose} title='Create New Advertiser' />
          <FormProvider {...form}>
            <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
              <AdvertiserInfo />
              <hr className='-mx-[32px] my-5' />
              <div className='mt-5 flex justify-end gap-[12px]'>
                <Button color='inherit' variant='outlined' onClick={handleClose}>
                  Cancel
                </Button>
                <Button color='primary' variant='contained' type='submit'>
                  Create
                </Button>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </LoadingBackdropWrapper>
  );
};

export default AddAdvertiserModal;
